import React, { useState } from "react";
import ProfileSidebar from "../../../components/reusable components/ProfileSidebar";
import { useAuth } from "../../../contexts/AuthContext";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import api, { postChangePassword } from "../../../lib/api";
import { deleteAccountForNow } from "../../../api/profile";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const EyeIcon = () => (<span className="material-icons text-[22px]">visibility</span>);
const EyeOffIcon = () => (<span className="material-icons text-[22px]">visibility_off</span>);

export default function AccountManagement() {
  const { user, forgot } = useAuth();
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [passwordError, setPasswordError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error" | ""; text: string }>({
    type: "",
    text: "",
  });
  const navigate=useNavigate()

  const displayEmail = user?.email || "";

  const handlePasswordChange = (field: string, value: string) => {
    setPasswords(prev => ({
      ...prev,
      [field]: value
    }));

    if (passwordError) { 
      setPasswordError('');
    }
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handlePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    if (passwords.new !== passwords.confirm) {
      setPasswordError("Passwords do not match");
      return;
    }

    if (passwords.new.length < 8) {
      setPasswordError("Password must be at least 8 characters long");
      return;
    }

    if (!passwords.current) {
      setPasswordError("Current password is required");
      return;
    }

    setIsSending(true);
    setPasswordError("");
    setMessage({ type: "", text: "" });

    try {
      // const { data } = await api.post("/users/profile/password-change/", {
      //   current_password: passwords.current,
      //   new_password: passwords.new,
      //   confirm_password: passwords.confirm,
      // });

      const current_password = passwords.current;
      const new_password = passwords.new;
      const confirm_password = passwords.confirm;
      const data = await postChangePassword(
        current_password,
        new_password,
        confirm_password,

        )

      // console.log("Password change response:", data);

      setMessage({
        type: "success",
        text: "Your password has been successfully updated.",
      });

      // Reset fields
      setPasswords({
        current: "",
        new: "",
        confirm: "",
      });
    } catch (e: any) {
      // console.error("Password change error:", error);
      toast.error(e?.response?.data?.error ||
        e?.response?.data?.message ||
         e?.response?.data?.detail||"Failed to change password")

      let msg = "Failed to change password.";
      if (e.response?.data) {
        const errData = e.response.data;
        msg =
          errData.detail ||
          errData.message ||
          errData.error ||
          Object.values(errData).flat().join(" ") ||
          msg;
      }

      setMessage({ type: "error", text: msg });
    } finally {
      setIsSending(false);
    }
  };

  const onChangePassword = async () => {
    if (!displayEmail) {
      setMessage({ type: "error", text: "No email address associated with your account." });
      return;
    }

    try {
      setIsSending(true);
      setMessage({ type: "", text: "" });

      await forgot(displayEmail);

      setMessage({
        type: "success",
        text: `A magic link has been sent to ${displayEmail}. Please check your inbox to reset your password.`,
      });
    } catch (error: any) {
      let msg = "Failed to send password reset email.";
      if (error.code === "auth/user-not-found") msg = "No account found with that email.";
      if (error.code === "auth/invalid-email") msg = "Invalid email address.";
      setMessage({ type: "error", text: msg });
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteAccount = async() => {
    const confirmed=window.confirm('Are you sure you want to delete your account? This action cannot be undone.')

    if (!confirmed) return;

  try {
    await deleteAccountForNow();
    // console.log("Account deletion requested");

    toast.success("Your account has been deleted");
    navigate("/login");
  } catch (error) {
    // console.error("Error deleting account:", error);
    toast.error("Failed to delete account. Please try again.");
  }
  };

  return (
    <div className="flex min-h-screen gap-5 p-3">
      <ProfileSidebar />
      <div className="p-6 h-full w-full overflow-y-auto bg-white rounded-xl border-l border-gray-200">
        <h1 className="text-2xl font-semibold">Account Management</h1>
        <p className="mt-2 mb-4 text-[#777B7F]">Manage your account preferences, profile details and app settings</p>

        <hr />
        
        {/* Change Password Section */}
        <div className="mt-0 py-6 bg-white rounded-lg shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Change Password</h2>

          <p>
            Get a{" "}
            <button
              className={`px-1 text-blue-600 hover:underline ${
                isSending ? "opacity-50 cursor-not-allowed" : ""
              }`}
              onClick={onChangePassword}
              disabled={isSending}
            >
              magic link
            </button>{" "}
            to change your password.
          </p>
          
          {isSending && (
            <p className="flex items-center gap-2 text-sm text-blue-600 font-medium mt-2">
              Sending...
            </p>
          )}

          {/* Feedback message */}
          {message.text && (
            <div
              className={`mt-3 flex items-center gap-2 text-sm ${
                message.type ==="success" ? "text-green-600" : "text-red-600"
              }`}
            >
              {message.type === "success" ? (
                <CheckCircle2 size={16} />
              ) : (
                <AlertCircle size={16} />
              )}
              <span>{message.text}</span>
            </div>
          )}

          <div className="mt-6">
            <h3 className="text-base font-medium text-gray-900 mb-4">Or change password manually:</h3>
            
            <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-md">
              {/* Current Password */}
              <div>
                <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <div className="relative w-full">
                  <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">
                    lock
                  </span>
                  <input
                    type={showPasswords.current ? "text" : "password"}
                    id="current-password"
                    value={passwords.current}
                    onChange={(e) => handlePasswordChange('current', e.target.value)}
                    className="w-full px-9 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#777B7F] focus:outline-none"
                    aria-label="Toggle current password visibility"
                  >
                    {showPasswords.current ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <div className="relative w-full">
                  <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">
                    lock
                  </span>
                  <input
                    type={showPasswords.new ? "text" : "password"}
                    id="new-password"
                    value={passwords.new}
                    onChange={(e) => handlePasswordChange('new', e.target.value)}
                    className="w-full px-9 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#777B7F] focus:outline-none"
                    aria-label="Toggle new password visibility"
                  >
                    {showPasswords.new ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <div className="relative">
                  <span className="material-icons absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">
                    lock
                  </span>
                  <input
                    type={showPasswords.confirm ? "text" : "password"}
                    id="confirm-password"
                    value={passwords.confirm}
                    onChange={(e) => handlePasswordChange('confirm', e.target.value)}
                    className="w-full px-9 py-2 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#777B7F] focus:outline-none"
                    aria-label="Toggle confirm password visibility"
                  >
                    {showPasswords.confirm ? <EyeOffIcon /> : <EyeIcon />}
                  </button>
                </div>
                {passwords.new && passwords.confirm && (
                  <div className="text-sm mt-2">
                    {passwords.new === passwords.confirm ? (
                      <p className="text-green-600">✓ Passwords match</p>
                    ) : (
                      <p className="text-red-600">✗ Passwords do not match</p>
                    )}
                  </div>
                )}
              </div>

              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{passwordError}</p>
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSending}
                className="bg-[#1D4B57] text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSending ? "Updating Password..." : "Update Password"}
              </button>
            </form>
          </div>
        </div>

        <hr />

        {/* Delete Account Section */}
        <div className="mt-2 pt-6 pb-2 bg-white">
          <h2 className="text-lg font-medium text-[#777B7F] mb-2">Delete Account</h2>
          <p className="text-[#777B7F] mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>

          <button
            onClick={handleDeleteAccount}
            className="text-[#141414] border border-[#0000001A] px-6 py-2 rounded-md text-sm font-medium hover:bg-red-50 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
}